import { ChangeDetectionStrategy, Component } from '@angular/core';
import { DataService, Dialog, NotificationService, SharedModule } from '@vendure/admin-ui/core';
import { CREATE_PARCEL_PROFILE, GET_PARCEL_PROFILE, UPDATE_PARCEL_PROFILE } from './gql/parcel-profile.graphql';
import { CreateParcelProfileInput, UpdateParcelProfileInput, ParcelProfile, MassUnit, DistanceUnit } from '../../common/ui/generated';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

type Mode = 'create' | 'detail';
@Component({
    selector: 'vdr-create-parcel-profile-dialog',
    template: `
        <ng-template vdrDialogTitle> {{ title }} </ng-template>
        <form [formGroup]="form">
            <vdr-form-field label="Name">
                <input type="text" formControlName="name" />
            </vdr-form-field>
            <vdr-form-field label="Parcel Weight">
                <input type="number" formControlName="weight" />
            </vdr-form-field>
            <vdr-form-field label="Max Weight">
                <input type="number" formControlName="maxWeight" />
            </vdr-form-field>
            <vdr-form-field label="Mass Unit">
                <select formControlName="massUnit">
                    <option [value]="MassUnit.g">Grams (g)</option>
                    <option [value]="MassUnit.oz">Ounces (oz)</option>
                </select>
            </vdr-form-field>
            <vdr-form-field label="Length">
                <input type="number" formControlName="length" />
            </vdr-form-field>
            <vdr-form-field label="Width">
                <input type="number" formControlName="width" />
            </vdr-form-field>
            <vdr-form-field label="Height">
                <input type="number" formControlName="height" />
            </vdr-form-field>
            <vdr-form-field label="Distance Unit">
                <select formControlName="distanceUnit">
                    <option [value]="DistanceUnit.cm">Centimeters (cm)</option>
                    <option [value]="DistanceUnit.in">Inches (in)</option>
                </select>
            </vdr-form-field>
        </form>
        <ng-template vdrDialogButtons>
            <button type="button" class="btn" (click)="cancel()">Cancel</button>
            <button type="submit" class="btn btn-primary" (click)="submit()" [disabled]="form.invalid">{{buttonText}}</button>
        </ng-template>
    `,
    standalone: true,
    imports: [SharedModule, ReactiveFormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ParcelProfileDialogComponent implements Dialog<ParcelProfile> {
    mode: Mode = 'create';
    profileId?: string;

    form: FormGroup;
    resolveWith: (result?: ParcelProfile) => void;
    MassUnit = MassUnit;
    DistanceUnit = DistanceUnit;
    loading = false;
    buttonText: string;
    title: string;

    constructor(
        private fb: FormBuilder,
        private dataService: DataService,
        private formBuilder: FormBuilder,
        private notificationService: NotificationService
    ) {
        this.form = this.formBuilder.group({
            name: ['', Validators.required],
            maxWeight: [0, [Validators.required, Validators.min(0)]],
            massUnit: [MassUnit.oz, Validators.required],
            weight: [0, [Validators.required, Validators.min(0)]],
            length: [0, [Validators.required, Validators.min(0)]],
            width: [0, [Validators.required, Validators.min(0)]],
            height: [0, [Validators.required, Validators.min(0)]],
            distanceUnit: [DistanceUnit.in, Validators.required]
        });
    }

    ngOnInit() {
        this.title = (this.mode === 'create') ? 'Create Parcel Profile' : 'Parcel Profile Details';
        if (this.mode === 'detail' && this.profileId) {
            this.loading = true;
            this.dataService
                .query(GET_PARCEL_PROFILE, { id: this.profileId })
                .mapSingle((data: any) => data.parcelProfile)
                .subscribe(profile => {
                    this.form.patchValue({
                        ...profile,
                        ...profile.dimension,
                        distanceUnit: profile.dimension.unit,
                    });
                    this.loading = false;
                });
        }
        this.buttonText = (this.mode === 'detail') ? 'Update' : 'Create';
    }

    submit() {
        if (this.form.valid) {
            const { name, length, width, height, distanceUnit, weight, maxWeight, massUnit } = this.form.value;
            const input: CreateParcelProfileInput | UpdateParcelProfileInput = {
                ...(this.mode === 'detail' ? { id: this.profileId! } : {}),
                name,
                dimension: {
                    length,
                    width,
                    height,
                    unit: distanceUnit === 'in' ? DistanceUnit.in : DistanceUnit.cm
                },
                weight,
                maxWeight,
                massUnit: massUnit === 'oz' ? MassUnit.oz : MassUnit.g
            };

            const mutation = this.mode === 'create' ? CREATE_PARCEL_PROFILE : UPDATE_PARCEL_PROFILE;

            this.dataService.mutate(mutation, {
                input
            }).subscribe((data: any) => {
                const result = data?.createParcelProfile || data?.updateParcelProfile;
                this.notificationService.success('Parcel profile saved');
                this.resolveWith(result as ParcelProfile);
            });
        }
    }

    cancel() {
        this.resolveWith();
    }
}
